import type { ScreenColour } from '@alwindows/shared/api/screens'

/**
 * State-restricted frame colours.
 *
 * SAP's /v1/screen/colours returns the same nine colours for every sales
 * org with `SalesOrg` blank on every row, so the API carries no usable
 * regional restriction. Picking a colour SAP won't sell in the customer's
 * state lets the quote save, then SAP rejects the quotation and the order
 * silently dies in the middleware (Ripley QLD, ref 5PY1677C8TKA,
 * 2026-09-16).
 *
 * The first fix parsed a "(VIC Only)" qualifier out of the colour name.
 * A&L asked us to stop relying on that (2026-09-21): the colour records are
 * shared with other parts of the website, so the descriptions can't be
 * maintained as a source of truth. The restrictions below are the ones on
 * the site's "Colour options" page and are keyed on the colour's base name
 * (description with any bracketed suffix removed, case-insensitive).
 * Colours not listed here are sold everywhere; an unknown customer state
 * hides nothing.
 */

const STATE_CODES = new Set(['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'])

/**
 * Colour base name → states it may be ordered in. Source: A&L "Colour
 * options" page, supplied 2026-09-21. Anodic Grey and Merino (SA only) are
 * included for completeness even though the screens endpoint doesn't
 * return them today.
 */
export const COLOUR_STATE_RESTRICTIONS: Readonly<Record<string, readonly string[]>> = {
  'anodic grey': ['SA'],
  dune: ['VIC', 'QLD', 'NSW'],
  jasper: ['VIC'],
  merino: ['SA'],
  'precious silver': ['VIC', 'QLD', 'NSW'],
  'shale grey': ['QLD', 'NSW'],
  'woodland grey': ['VIC', 'QLD', 'NSW'],
}

/**
 * The colour name SAP's Desc is compared on: trimmed, lower-cased, with any
 * bracketed suffix ("(VIC Only)", "®") dropped. Exported so the
 * configurator's home-colour pre-select can match on the same key.
 */
export function colourBaseName(desc: string): string {
  return desc
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/[®™]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/**
 * States a colour is restricted to. Empty when it's sold everywhere.
 */
export function colourRestrictedToStates(desc: string): string[] {
  return [...(COLOUR_STATE_RESTRICTIONS[colourBaseName(desc)] ?? [])]
}

export function colourAvailableInState(desc: string, state: string): boolean {
  const restricted = colourRestrictedToStates(desc)
  if (restricted.length === 0) return true
  const s = state.trim().toUpperCase()
  // No usable state on the quote: don't hide anything rather than hide
  // colours the customer may legitimately be able to buy.
  if (!STATE_CODES.has(s)) return true
  return restricted.includes(s)
}

export function filterColoursForState(colours: ScreenColour[], state: string): ScreenColour[] {
  return colours.filter((c) => colourAvailableInState(c.Desc, state))
}
